<script>
	import LineChart from "../../LineChart/LineChart.svelte";
	import { getBrightness, darken, rgbStringToHex } from "../../utils";
	import { formatInt } from "../../map/language";

	export let active;
	export let region;
	export let tooltipText;
	export let tooltipSubtitle = "";
	export let id;
	export let formatValue;
	export let colorScale;
	export let avgInRing;
	export let avgOutRing;
	export let rank;
	export let color;

	const maxDomainValue = colorScale.domain()[colorScale.domain().length - 1];

	const gradient =
		"linear-gradient(to right, " +
		colorScale
			.range()
			.map((color, index) => {
				const domainValue = colorScale.domain()[index];
				const percent = (domainValue / maxDomainValue) * 100; // Assuming 0.045 is your max value in domain
				return `${color} ${percent}%`;
			})
			.join(", ") +
		")";

	const positionInRing = (avgInRing / maxDomainValue) * 100;
	const positionOutRing = (avgOutRing / maxDomainValue) * 100;

	$: currentPercent = (region[id] / maxDomainValue) * 100;

	const ensureReadableOnWhite = (color) => {
		// Calculate the perceived brightness of the color.
		let brightness = getBrightness(color);

		if (brightness > 180) {
			const hex = color.startsWith("#") ? color : rgbStringToHex(color);
			let darkenedColor = darken(hex, 0.1); // you can adjust the amount to darken as needed
			return darkenedColor.toString();
		} else {
			return color;
		}
	};

	$: color = ensureReadableOnWhite(colorScale(region[id]));
	$: readableColor = ensureReadableOnWhite(color);

	let trend;
	$: {
		if (region[id] < -0.2) {
			trend = "muchmuchless";
		} else if (region[id] < -0.1) {
			trend = "less";
		} else if (region[id] < 0) {
			trend = "less";
		} else if (region[id] < 0.02) {
			trend = "lesssimilar";
		} else if (region[id] < 0.05) {
			trend = "similar";
		} else if (region[id] < 0.1) {
			trend = "more";
		} else if (region[id] < 0.2) {
			trend = "muchmore";
		} else {
			trend = "muchmuchmore";
		}
	}

	const frmtTotal = formatInt();
	$: totalValueText =
		region["2015"] && region["2022"]
			? ` Sie ${region.change > 0 ? "stieg" : "sank"} von ${frmtTotal(region["2015"])} im Jahr 2015 auf ${frmtTotal(
					region["2022"]
			  )} in 2022.`
			: "";
	$: subtitle = tooltipSubtitle[trend] ? tooltipSubtitle[trend] + totalValueText : `Rang ${rank}/542`;
</script>

<div class="section" {id} class:active style:border-left-color={active ? color : null} on:click>
	<div class="section__top">
		<div class="section__top__left">
			<div class="section__title">{@html tooltipText}</div>
		</div>
		<div class="section__top__right" style="--color: {readableColor}">
			{formatValue(region[id])}
		</div>
	</div>
	<div class="section__subtitle">{@html subtitle}</div>
	<div class="section__content" id="section-{id}">
		{#if id === "change"}
			{#if region.noPkwOrResidents}
				<div class="no-data">Keine PKWs oder Einwohner in dieser Region.</div>
			{:else}
				<LineChart data={region} {colorScale} />
			{/if}
		{:else}
			<div class="bar-wrapper">
				<div class="bar" style:background={gradient} />
				<!-- <div class="gradient right" /> -->
			</div>
			<div class="bar-values">
				<div
					class="value-wrapper in-ring"
					class:left-aligned={id === "pkwarea"}
					class:right-aligned={id === "pkwpp" || id === "newpp" || id === "oldpp"}
					style:left="{positionInRing}%"
				>
					<div class="line" />
					<div class="value">
						{formatValue(avgInRing)}
					</div>
					<div class="description">⌀ Berlin im Ring</div>
				</div>
				<div
					class="value-wrapper out-ring"
					class:left-aligned={id === "pkwpp" || id === "newpp" || id === "oldpp"}
					class:right-aligned={id === "pkwarea"}
					style:left="{positionOutRing}%"
				>
					<div class="line" />
					<div class="value">
						{formatValue(avgOutRing)}
					</div>
					<div class="description">⌀ außerhalb Ring</div>
				</div>
			</div>
			<div class="pointer" style:left="{Math.min(currentPercent, 100)}%">
				<div class="triangle-wrapper">
					<div class="triangle border" />
					<div class="triangle fill" style:border-top-color={color} />
				</div>
				<div class="line-background" />
				<div class="line" />
			</div>
		{/if}
	</div>
</div>

<style lang="scss">
	.section__content {
		position: relative;
		margin: 0px 10px;
	}

	.pointer {
		position: absolute;
		top: 0;
		display: flex;
		flex-direction: column;
		align-items: center;
		transform: translate(-50%, -17px);
		transition: left 0.3s ease-in-out;

		.line {
			width: 1px;
			height: 28px;
			background: $black;

			&:before {
				content: "";
				display: block;
				width: 3px;
				background: #fff;
				height: 100%;
				position: relative;
				z-index: -1;
				transform: translateX(-1px);
			}
		}

		.triangle-wrapper {
			display: flex;

			.triangle {
				width: 0px;
				height: 0px;
				border-style: solid;
				border-width: 9px 8px 0 8px;
				border-color: transparent;
				transform: rotate(0deg);

				&.border {
					border-color: $black transparent transparent transparent;
					transform: scale(1.4);
					transform-origin: top center;
				}

				&.fill {
					position: absolute;
					transform: translateY(1.5px);
					transition: border-top-color 0.3s ease-in-out;
				}
			}
		}
	}

	$width: 100%;

	.bar-wrapper {
		position: relative;
		width: $width;
		margin-top: 3px;

		.bar {
			height: 12px;
		}

		.gradient {
			position: absolute;
			right: 0;
			top: 0;
			width: 10%;
			height: 100%;
			background-image: linear-gradient(to right, rgba(255, 255, 255, 0) 0%, #ffffff 80%);
		}
	}

	.bar-values {
		position: relative;
		height: 35px;
		width: $width;

		.value-wrapper {
			position: absolute;
			font-size: 12px;
			font-weight: $medium;
			line-height: 110%;
			color: #767676;
			transform: translateX(-50%);
			display: flex;
			flex-direction: column;
			align-items: center;

			.line {
				height: 8px;
				width: 1px;
				background-color: #767676;
				margin-bottom: 2px;
			}

			&.right-aligned {
				align-items: flex-end;
				transform: translateX(-100%);
			}

			&.left-aligned {
				align-items: flex-start;
				transform: translateX(0%);
			}

			.description {
				white-space: nowrap;
			}
		}
	}

	.section {
		filter: grayscale(100%);
		opacity: 0.5;
		border-radius: 9px;
		padding: 8px 0;
		padding-left: 8px;
		padding-right: 8px;
		border-left: 3px solid transparent;
		margin-left: -10px;
		margin-right: -10px;
		transition: border-left-color 0.3s ease-in-out, filter 0.3s ease-in-out, opacity 0.3s ease-in-out;
		cursor: pointer;

		&.active {
			filter: none;
			opacity: 1;
			box-shadow: 0px 0px 12px 0px rgba(0, 0, 0, 0.15);
		}

		&__top {
			display: flex;
			justify-content: space-between;
			margin-bottom: 2px;

			&__right {
				font-weight: 600;
				font-size: 20px;
				line-height: 24px;
				text-align: right;
				color: var(--color);
			}

			&__left {
				align-self: flex-end;
			}
		}

		&__title {
			font-weight: 600;
			font-size: 13px;
			line-height: 120%;
			letter-spacing: 0.05em;
			text-transform: uppercase;
		}

		&__subtitle {
			font-weight: 400;
			font-size: 13px;
			line-height: 16px;
			color: #767676;
			margin-bottom: 19px;
		}

		&#change {
			.section__subtitle {
				margin-bottom: 8px;
				height: 64px;
			}
		}

		.no-data {
			color: #767676;
			font-size: 13px;
			height: 85px;
		}
	}
</style>
